// React, Redux y React Router DOM
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Componentes
import Login from "../../pages/login/LoginView";
import BrandLogo from "../../components/base/BrandLogo/BrandLogo";

// Utils
import { isAuthenticated } from "../../utils/tokenUtils";
import { resolveLandingRoute } from "../../config/appsConfig";
import { captureNextPath, nextFromSearch } from "../../utils/nextPath";
import { applyLangParam } from "../../utils/langParam";
import { isPlatformAdminRoleId } from "../../hooks/useIsReseller";
import { WEB_SERVICE_ROLE_ID } from "../../hooks/useIsWebServiceRole";

// Estilos
import {
  ContainerHome,
  ContainerLeftMap,
  ContainerLogin,
  ContainerLogoOverlay,
} from "./styles";

import EscenaLogin from "./EscenaLogin";

const BRAND_LOADER_MIN_MS = 900;

const BrandLoader = () => (
  <div className="relative w-screen h-screen overflow-hidden flex items-center justify-center bg-gradient-to-br from-app via-surface to-subtle">
    <div className="absolute h-[32rem] w-[32rem] rounded-full bg-primary-500/20 blur-3xl" />
    <div className="absolute -bottom-40 -right-32 h-[26rem] w-[26rem] rounded-full bg-primary-500/10 blur-3xl" />
    <BrandLogo
      variant="main"
      alt=""
      className="relative w-56 sm:w-72 h-auto animate-pulse"
    />
  </div>
);

const HomeView = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [isEnglish, setIsEnglish] = useState<boolean>(i18n.language === "en");
  const [isCheckingSession, setIsCheckingSession] = useState<boolean>(true);
  const [brandLoaderDone, setBrandLoaderDone] = useState<boolean>(false);

  useEffect(() => {
    const timer = setTimeout(
      () => setBrandLoaderDone(true),
      BRAND_LOADER_MIN_MS,
    );
    return () => clearTimeout(timer);
  }, []);

  // Verificar si hay una sesión válida al cargar
  useEffect(() => {
    const checkExistingSession = () => {
      // `?lang=` y `?next=` (CC-CONSOLE#1561): el idioma se aplica y el
      // `next` validado se guarda para después del login.
      applyLangParam(window.location.search);
      // Con sesión sólo redirige un `next` de ESTA URL (no uno viejo guardado).
      const next = nextFromSearch(window.location.search);
      if (next) captureNextPath(window.location.search);
      if (isAuthenticated() && next) {
        void import("../onboarding/onboardingService")
          .then(({ resolveNextOr }) => resolveNextOr(resolveLandingRoute()))
          .then((path) => navigate(path, { replace: true }));
        return;
      }
      if (isAuthenticated()) {
        const userRole = localStorage.getItem("userRole");
        const userRoleId = localStorage.getItem("userRoleId");

        // Redirigir según el rol. web_services (por role_id) va a /map;
        // web_services (legacy) y reseller a /admin/units; el resto usa la
        // pantalla de inicio configurada (defaultLanding) o el grid de apps
        // (/apps) vía resolveLandingRoute().
        if (userRoleId === WEB_SERVICE_ROLE_ID) {
          navigate("/map", { replace: true });
        } else if (
          userRole === "web_services" ||
          isPlatformAdminRoleId(userRoleId)
        ) {
          navigate("/admin/units", { replace: true });
        } else {
          navigate(resolveLandingRoute(), { replace: true });
        }
      } else {
        setIsCheckingSession(false);
      }
    };

    checkExistingSession();
  }, [navigate]);

  useEffect(() => {
    setIsEnglish(i18n.language === "en");
  }, [i18n.language]);

  if (isCheckingSession || !brandLoaderDone) {
    return <BrandLoader />;
  }

  return (
    <ContainerHome>
      <ContainerLeftMap>
        <EscenaLogin />
        {/* Mostrar el logo solo en desktop/laptop */}
        <ContainerLogoOverlay className="hidden md:flex">
          <BrandLogo variant="main" alt="Logo" className="w-40 h-auto" />
        </ContainerLogoOverlay>
      </ContainerLeftMap>
      <ContainerLogin>
        <Login isEnglish={isEnglish} setIsEnglish={setIsEnglish} />
      </ContainerLogin>
    </ContainerHome>
  );
};

export default HomeView;
